/**
 * Devices view — one location card (#999 Story 3.5).
 *
 * Styled like a form card. Renders a tag's assigned units grouped by equipment
 * type, or an "assign equipment" prompt when the tag has none. The bottom
 * Untagged card reuses this same shell (`card.isUntagged`).
 *
 * Header is an accordion bar (pin + name + unit-count badge + chevron). Body
 * sits on the white card surface below.
 */

import React, { useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { DeviceTypeGroup } from './DeviceTypeGroup';
import { colors, spacing } from '../../../theme';
import { ChevronDown, ChevronRight, MapPin } from '../../icons';

import type { DeviceSourceUnit } from '../../../utils/deviceRowMapper';
import type {
  DeviceLocationCard,
  DeviceStepId,
} from '../../../utils/devicesViewModel';

const UNTAGGED_TITLE = 'Untagged';
const EMPTY_TAG_PROMPT = 'No devices assigned to this location yet.';

interface LocationCardProps {
  card: DeviceLocationCard;
  onUnitPress?: (stepId: DeviceStepId, unit: DeviceSourceUnit) => void;
  onReorderUnits?: (
    stepId: DeviceStepId,
    visibleUnits: readonly DeviceSourceUnit[],
    fromIndex: number,
    toIndex: number,
  ) => void;
  canReorderStep?: (stepId: DeviceStepId) => boolean;
  resolveLocationTagName?: (
    locationTagId: number | null | undefined,
  ) => string | null;
  testID?: string;
}

export const LocationCard: React.FC<LocationCardProps> = ({
  card,
  onUnitPress,
  onReorderUnits,
  canReorderStep,
  resolveLocationTagName,
  testID,
}) => {
  const [expanded, setExpanded] = useState(true);

  const title = card.isUntagged
    ? UNTAGGED_TITLE
    : card.locationTagName ?? UNTAGGED_TITLE;
  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <View style={styles.card} testID={testID}>
      <Pressable
        onPress={() => setExpanded(prev => !prev)}
        style={({ pressed }) => [
          styles.header,
          pressed && styles.headerPressed,
        ]}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        accessibilityLabel={title}
        accessibilityHint={
          expanded ? 'Collapses this location' : 'Expands this location'
        }
        testID={testID ? `${testID}-toggle` : undefined}
      >
        <View
          style={[styles.iconWrap, card.isUntagged && styles.iconWrapUntagged]}
        >
          <MapPin
            size={16}
            color={card.isUntagged ? colors.textMuted : colors.primary}
          />
        </View>
        <Text style={styles.title} numberOfLines={1}>
          {title}
        </Text>
        <Chevron size={18} color={colors.textMuted} />
      </Pressable>

      {expanded ? (
        <View style={styles.body}>
          {card.isEmpty ? (
            <Text
              style={styles.emptyPrompt}
              testID={testID ? `${testID}-empty` : undefined}
            >
              {EMPTY_TAG_PROMPT}
            </Text>
          ) : (
            <>
              {card.groups.map(group => (
                <DeviceTypeGroup
                  key={group.stepId}
                  group={group}
                  onUnitPress={onUnitPress}
                  onReorderUnits={onReorderUnits}
                  canReorderStep={canReorderStep}
                  resolveLocationTagName={resolveLocationTagName}
                  testID={
                    testID ? `${testID}-group-${group.stepId}` : undefined
                  }
                />
              ))}
            </>
          )}
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderColor: colors.borderCard,
    borderRadius: spacing.cardBorderRadius,
    backgroundColor: colors.backgroundPrimary,
    marginBottom: spacing.pdStepSubsectionGap,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.cardPadding,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderCard,
  },
  headerPressed: {
    opacity: 0.7,
  },
  iconWrap: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccentMuted,
  },
  iconWrapUntagged: {
    backgroundColor: colors.backgroundMuted,
  },
  title: {
    flex: 1,
    minWidth: 0,
    fontSize: 16,
    fontWeight: '700',
    color: colors.textPrimary,
  },
  body: {
    paddingHorizontal: spacing.cardPadding,
    paddingBottom: spacing.cardPadding,
  },
  emptyPrompt: {
    fontSize: 13,
    color: colors.textSecondary,
    marginTop: spacing.sm,
  },
});
